import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Box, Button, Card, CardContent, CircularProgress, Tab, Tabs, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  acceptKvkk,
  getCorporateEmployeeById,
  getCorporateEmployees,
  getCostCenterOptions,
  getPolicyOptions,
} from '../../../../api/endpoints/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { DetailField, DetailSection, PageHeader, StatusChip } from '../../../../components/common';
import { useSetBreadcrumbLeaf } from '../../../../layout/breadcrumbContext';
import { EmployeeFormDialog } from '../components/EmployeeFormDialog';
import { IdCardSection } from '../components/IdCardSection';
import { LoyaltySection } from '../components/LoyaltySection';
import { ManagerAssignDialog } from '../components/ManagerAssignDialog';
import { PassportSection } from '../components/PassportSection';

/** Personel detay: özet bilgiler + yönetici ataması + KVKK durumu + Kimlik Kartı / Pasaport
 *  Belgesi sub-formları. */
export default function EmployeeDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const id = Number(employeeId);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [kvkkConfirmOpen, setKvkkConfirmOpen] = useState(false);

  const query = useQuery({
    queryKey: ['corporateEmployee', 'byId', id],
    queryFn: () => getCorporateEmployeeById(id),
    enabled: Number.isFinite(id) && id > 0,
  });
  // Reuses the list page's cache keys so these dropdowns don't fire extra requests when
  // navigating here from the list page.
  const employeesQuery = useQuery({ queryKey: ['corporateEmployee', 'list'], queryFn: () => getCorporateEmployees() });
  const policiesQuery = useQuery({ queryKey: ['corporateEmployee', 'policyOptions'], queryFn: () => getPolicyOptions() });
  const costCentersQuery = useQuery({
    queryKey: ['corporateEmployee', 'costCenterOptions'],
    queryFn: () => getCostCenterOptions(),
  });

  const employee = query.data?.corporateEmployeeList?.[0] ?? null;
  useSetBreadcrumbLeaf(employee ? `${employee.name ?? ''} ${employee.surName ?? ''}`.trim() : null);
  const candidates = employeesQuery.data?.corporateEmployeeList ?? [];
  const policies = policiesQuery.data?.policies ?? [];
  const costCenters = costCentersQuery.data?.costCenter ?? [];

  const kvkkMutation = useMutation({
    mutationFn: () => acceptKvkk({ kvkkAccepted: true, kvkkVersion: '1.0' }),
    onSuccess: () => {
      enqueueSnackbar('KVKK onayı kaydedildi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'byId', id] });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'list'] });
      setKvkkConfirmOpen(false);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <PageHeader
        title={employee ? `${employee.name ?? ''} ${employee.surName ?? ''}`.trim() : 'Personel Detayı'}
        backTo="/corporate/employees"
        subtitle={employee ? `${employee.emailAdress || '—'} · ${employee.phoneNumber || '—'}` : undefined}
        status={employee ? <StatusChip active={employee.isActive} /> : undefined}
        actions={
          employee ? (
            <>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Düzenle
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SupervisorAccountIcon />}
                onClick={() => setManagerDialogOpen(true)}
              >
                Yönetici Ata
              </Button>
            </>
          ) : undefined
        }
      />

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {employee && (
        <Card sx={{ mb: 3 }} variant="outlined">
          <CardContent>
            <DetailSection title="Özet">
              <DetailField label="Sicil No" value={employee.registrationNumber} />
              <DetailField label="Politika" value={employee.policyName} />
              <DetailField label="Masraf Merkezi" value={employee.costCenterName} />
              <DetailField label="Yönetici" value={employee.managerName} />
              <DetailField
                label="Rol"
                value={
                  employee.isProgramUser
                    ? 'Satın Alma Yapabilir'
                    : employee.isSp
                      ? 'Seyahat Planlamacısı'
                      : null
                }
              />
              <DetailField
                label="KVKK Durumu"
                value={
                  <StatusChip
                    tone={employee.kvkkAccepted ? 'success' : 'warning'}
                    label={
                      employee.kvkkAccepted
                        ? `Onaylı${employee.kvkkAcceptedAt ? ` (${employee.kvkkAcceptedAt.slice(0, 10)})` : ''}`
                        : 'Onay Bekliyor'
                    }
                  />
                }
              />
            </DetailSection>
            {!employee.kvkkAccepted && (
              <Tooltip title="Yalnızca oturum sahibinin kendi bağlı personel kaydı için çalışır (gerçek API'de EmployeeID alanı yoktur).">
                <span>
                  <Button size="small" sx={{ mt: 1 }} onClick={() => setKvkkConfirmOpen(true)}>
                    KVKK Onayını Kaydet
                  </Button>
                </span>
              </Tooltip>
            )}
          </CardContent>
        </Card>
      )}

      {employee && (
        <Box>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Kimlik Kartları" />
            <Tab label="Pasaport Belgeleri" />
            <Tab label="Sadakat Programları" />
          </Tabs>
          {tab === 0 && <IdCardSection employeeID={employee.employeeID} />}
          {tab === 1 && <PassportSection employeeID={employee.employeeID} />}
          {tab === 2 && <LoyaltySection employeeID={employee.employeeID} />}
        </Box>
      )}

      <EmployeeFormDialog
        open={editOpen}
        employee={employee}
        policies={policies}
        costCenters={costCenters}
        onClose={() => setEditOpen(false)}
      />
      <ManagerAssignDialog
        open={managerDialogOpen}
        employee={employee}
        candidates={candidates}
        onClose={() => setManagerDialogOpen(false)}
      />

      <ConfirmDialog
        open={kvkkConfirmOpen}
        title="KVKK onayını kaydet"
        description="Bu işlem yalnızca oturum sahibinin kendi bağlı personel kaydı için çalışır. Devam etmek istiyor musunuz?"
        confirmLabel="Onayla"
        loading={kvkkMutation.isPending}
        onConfirm={() => kvkkMutation.mutate()}
        onCancel={() => setKvkkConfirmOpen(false)}
      />
    </Box>
  );
}
